import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { InputWithAlert } from "../components/ui/input-with-alert";
import { DatePicker } from "../components/ui/date-picker";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { format, isBefore } from "date-fns";
import KwestionariushProgressBar from "../components/kwestionariush/KwestionariushProgressBar";
import { createKwestionariush, getUser } from "../service/weexpertService";
import { toast } from "@/components/ui/use-toast";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { validateOnlyLatin } from "@/constants";

const FORM_STORAGE_KEY = "kwestionariush_form_data";

export default function KwestionariushPage() {
  const { t } = useTranslation("questionnaire");
  const navigate = useNavigate();
  const [currentStage, setCurrentStage] = useState(1);
  const [inputChecked, setInputChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const queryClient = useQueryClient();
  const { data: userData } = useQuery({ queryKey: "userMe", queryFn: getUser });

  // Stage 1 - Personal Information
  const [birthDate, setBirthDate] = useState(null);

  useEffect(() => {
  if (userData?.data?.dateOfBirth) {
    setBirthDate(new Date(userData.data.dateOfBirth));
  }
}, [userData]);

  const [birthPlace, setBirthPlace] = useState("");
  const [registrationAddress, setRegistrationAddress] = useState("");
  const [taxResidenceAddress, setTaxResidenceAddress] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [nfzBranch, setNfzBranch] = useState("");

  // Stage 2 - Declarations
  const [isEmployed, setIsEmployed] = useState(false);
  const [employmentPeriodFrom, setEmploymentPeriodFrom] = useState(null);
  const [employmentPeriodTo, setEmploymentPeriodTo] = useState(null);
  const [salaryLevel, setSalaryLevel] = useState("");
  const [onLeave, setOnLeave] = useState(false);
  const [leavePeriodFrom, setLeavePeriodFrom] = useState(null);
  const [leavePeriodTo, setLeavePeriodTo] = useState(null);

  const [hasOtherContract, setHasOtherContract] = useState(false);
  const [otherContractFrom, setOtherContractFrom] = useState(null);
  const [otherContractTo, setOtherContractTo] = useState(null);
  const [otherContractExceedsMinimum, setOtherContractExceedsMinimum] =
    useState(false);

  const [hasOtherInsurance, setHasOtherInsurance] = useState(false);
  const [otherInsuranceTitle, setOtherInsuranceTitle] = useState("");

  const [isPensioner, setIsPensioner] = useState(false);
  const [pensionDecisionNumber, setPensionDecisionNumber] = useState("");

  const [hasDisability, setHasDisability] = useState(false);
  const [disabilityLevel, setDisabilityLevel] = useState("");
  const [disabilityFrom, setDisabilityFrom] = useState(null);
  const [disabilityTo, setDisabilityTo] = useState(null);

  const [isStudent, setIsStudent] = useState(false);
  const [isUnemployed, setIsUnemployed] = useState(false);
  const [hasSocialInsurance, setHasSocialInsurance] = useState(false);

  const [wantsVoluntaryHealthInsurance, setWantsVoluntaryHealthInsurance] =
    useState(false);
  const [wantsVoluntaryPensionInsurance, setWantsVoluntaryPensionInsurance] =
    useState(false);

  const [wantsTaxExemption, setWantsTaxExemption] = useState(false);

  const [hasTaxResidencyCertificate, setHasTaxResidencyCertificate] =
    useState(false);
  const [taxResidencyCertificateFrom, setTaxResidencyCertificateFrom] =
    useState(null);
  const [taxResidencyCertificateTo, setTaxResidencyCertificateTo] =
    useState(null);

  const [exceedsAuthorCostLimit, setExceedsAuthorCostLimit] = useState(false);
  const [authorCostAmount, setAuthorCostAmount] = useState("");

  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);

  // Load saved form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Restore Stage 1
        setCurrentStage(parsedData.currentStage || 1);
        setBirthDate(
          parsedData.birthDate ? new Date(parsedData.birthDate) : null
        );
        setBirthPlace(parsedData.birthPlace || "");
        setRegistrationAddress(parsedData.registrationAddress || "");
        setTaxResidenceAddress(parsedData.taxResidenceAddress || "");
        setBankAccountNumber(parsedData.bankAccountNumber || "");
        setTaxOffice(parsedData.taxOffice || "");
        setNfzBranch(parsedData.nfzBranch || "");

        // Restore Stage 2
        setIsEmployed(parsedData.isEmployed || false);
        setEmploymentPeriodFrom(
          parsedData.employmentPeriodFrom
            ? new Date(parsedData.employmentPeriodFrom)
            : null
        );
        setEmploymentPeriodTo(
          parsedData.employmentPeriodTo
            ? new Date(parsedData.employmentPeriodTo)
            : null
        );
        setSalaryLevel(parsedData.salaryLevel || "");
        setOnLeave(parsedData.onLeave || false);
        setLeavePeriodFrom(
          parsedData.leavePeriodFrom
            ? new Date(parsedData.leavePeriodFrom)
            : null
        );
        setLeavePeriodTo(
          parsedData.leavePeriodTo ? new Date(parsedData.leavePeriodTo) : null
        );

        setHasOtherContract(parsedData.hasOtherContract || false);
        setOtherContractFrom(
          parsedData.otherContractFrom
            ? new Date(parsedData.otherContractFrom)
            : null
        );
        setOtherContractTo(
          parsedData.otherContractTo
            ? new Date(parsedData.otherContractTo)
            : null
        );
        setOtherContractExceedsMinimum(
          parsedData.otherContractExceedsMinimum || false
        );

        setHasOtherInsurance(parsedData.hasOtherInsurance || false);
        setOtherInsuranceTitle(parsedData.otherInsuranceTitle || "");

        setIsPensioner(parsedData.isPensioner || false);
        setPensionDecisionNumber(parsedData.pensionDecisionNumber || "");

        setHasDisability(parsedData.hasDisability || false);
        setDisabilityLevel(parsedData.disabilityLevel || "");
        setDisabilityFrom(
          parsedData.disabilityFrom ? new Date(parsedData.disabilityFrom) : null
        );
        setDisabilityTo(
          parsedData.disabilityTo ? new Date(parsedData.disabilityTo) : null
        );

        setIsStudent(parsedData.isStudent || false);
        setIsUnemployed(parsedData.isUnemployed || false);
        setHasSocialInsurance(parsedData.hasSocialInsurance || false);

        setWantsVoluntaryHealthInsurance(
          parsedData.wantsVoluntaryHealthInsurance || false
        );
        setWantsVoluntaryPensionInsurance(
          parsedData.wantsVoluntaryPensionInsurance || false
        );

        setWantsTaxExemption(parsedData.wantsTaxExemption || false);

        setHasTaxResidencyCertificate(
          parsedData.hasTaxResidencyCertificate || false
        );
        setTaxResidencyCertificateFrom(
          parsedData.taxResidencyCertificateFrom
            ? new Date(parsedData.taxResidencyCertificateFrom)
            : null
        );
        setTaxResidencyCertificateTo(
          parsedData.taxResidencyCertificateTo
            ? new Date(parsedData.taxResidencyCertificateTo)
            : null
        );

        setExceedsAuthorCostLimit(parsedData.exceedsAuthorCostLimit || false);
        setAuthorCostAmount(parsedData.authorCostAmount || "");

        setDataProcessingConsent(parsedData.dataProcessingConsent || false);

        setInputChecked(false);
      } catch (error) {
        console.error("Failed to load saved form data:", error);
      }
    }
    setIsLoading(false);
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    if (!isLoading) {
      const formData = {
        currentStage,
        // Stage 1
        birthDate: birthDate ? birthDate.toISOString() : null,
        birthPlace,
        registrationAddress,
        taxResidenceAddress,
        bankAccountNumber,
        taxOffice,
        nfzBranch,
        // Stage 2
        isEmployed,
        employmentPeriodFrom: employmentPeriodFrom
          ? employmentPeriodFrom.toISOString()
          : null,
        employmentPeriodTo: employmentPeriodTo
          ? employmentPeriodTo.toISOString()
          : null,
        salaryLevel,
        onLeave,
        leavePeriodFrom: leavePeriodFrom ? leavePeriodFrom.toISOString() : null,
        leavePeriodTo: leavePeriodTo ? leavePeriodTo.toISOString() : null,
        hasOtherContract,
        otherContractFrom: otherContractFrom
          ? otherContractFrom.toISOString()
          : null,
        otherContractTo: otherContractTo ? otherContractTo.toISOString() : null,
        otherContractExceedsMinimum,
        hasOtherInsurance,
        otherInsuranceTitle,
        isPensioner,
        pensionDecisionNumber,
        hasDisability,
        disabilityLevel,
        disabilityFrom: disabilityFrom ? disabilityFrom.toISOString() : null,
        disabilityTo: disabilityTo ? disabilityTo.toISOString() : null,
        isStudent,
        isUnemployed,
        hasSocialInsurance,
        wantsVoluntaryHealthInsurance,
        wantsVoluntaryPensionInsurance,
        wantsTaxExemption,
        hasTaxResidencyCertificate,
        taxResidencyCertificateFrom: taxResidencyCertificateFrom
          ? taxResidencyCertificateFrom.toISOString()
          : null,
        taxResidencyCertificateTo: taxResidencyCertificateTo
          ? taxResidencyCertificateTo.toISOString()
          : null,
        exceedsAuthorCostLimit,
        authorCostAmount,
        dataProcessingConsent,
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    }
  }, [
    isLoading,
    currentStage,
    birthDate,
    birthPlace,
    registrationAddress,
    taxResidenceAddress,
    bankAccountNumber,
    taxOffice,
    nfzBranch,
    isEmployed,
    employmentPeriodFrom,
    employmentPeriodTo,
    salaryLevel,
    onLeave,
    leavePeriodFrom,
    leavePeriodTo,
    hasOtherContract,
    otherContractFrom,
    otherContractTo,
    otherContractExceedsMinimum,
    hasOtherInsurance,
    otherInsuranceTitle,
    isPensioner,
    pensionDecisionNumber,
    hasDisability,
    disabilityLevel,
    disabilityFrom,
    disabilityTo,
    isStudent,
    isUnemployed,
    hasSocialInsurance,
    wantsVoluntaryHealthInsurance,
    wantsVoluntaryPensionInsurance,
    wantsTaxExemption,
    hasTaxResidencyCertificate,
    taxResidencyCertificateFrom,
    taxResidencyCertificateTo,
    exceedsAuthorCostLimit,
    authorCostAmount,
    dataProcessingConsent,
  ]);

  // Business logic: Employment affects social insurance and unemployment status
  useEffect(() => {
    if (isEmployed) {
      // If employed, automatically check social insurance
      setHasSocialInsurance(true);
      // If employed, cannot be unemployed - uncheck unemployment
      setIsUnemployed(false);
    } else {
      // Reset social insurance when employment is unchecked
      setHasSocialInsurance(false);
    }
  }, [isEmployed]);

  // Helper function to handle number-only input for author costs
  const handleAuthorCostAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers, decimal point, and spaces (for Polish formatting)
    const numberOnlyRegex = /^[0-9\s.,]*$/;

    if (numberOnlyRegex.test(value) || value === "") {
      setAuthorCostAmount(value);
    }
    // If regex test fails, the input is ignored (character not added)
  };

  // Helper function to validate age
  const validateAge = (birthDate) => {
    if (!birthDate) return false;
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      return age - 1 >= 19;
    } else {
      return age >= 19;
    }
  };

  const validateStage1 = () => {
    const ageValid = validateAge(birthDate);
    return (
      birthDate &&
      ageValid &&
      birthPlace.trim() &&
      registrationAddress.trim() &&
      taxResidenceAddress.trim() &&
      bankAccountNumber.trim() &&
      taxOffice.trim() &&
      nfzBranch.trim()
    );
  };

  const validateStage2 = () => {
    // Data processing consent is always required
    if (!dataProcessingConsent) {
      return false;
    }

    // Employment validation - if selected, require period dates and salary level
    if (isEmployed) {
      if (!employmentPeriodFrom || !employmentPeriodTo || !salaryLevel) {
        return false;
      }
      // If on leave is checked, require leave period dates
      if (onLeave && (!leavePeriodFrom || !leavePeriodTo)) {
        return false;
      }
    }

    // Other contracts validation - if selected, require period dates
    if (hasOtherContract) {
      if (!otherContractFrom || !otherContractTo) {
        return false;
      }
    }

    // Other insurance validation - if selected, require title
    if (hasOtherInsurance) {
      if (!otherInsuranceTitle.trim()) {
        return false;
      }
    }

    // Pensioner validation - if selected, require decision number
    if (isPensioner) {
      if (!pensionDecisionNumber.trim()) {
        return false;
      }
    }

    // Disability validation - if selected, require level and period dates
    if (hasDisability) {
      if (!disabilityLevel || !disabilityFrom || !disabilityTo) {
        return false;
      }
    }

    // Tax residency certificate validation - if selected, require period dates
    if (hasTaxResidencyCertificate) {
      if (!taxResidencyCertificateFrom || !taxResidencyCertificateTo) {
        return false;
      }
    }

    // Author costs validation - if selected, require amount
    if (exceedsAuthorCostLimit) {
      if (!authorCostAmount.trim()) {
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    setInputChecked(true);

    if (currentStage === 1 && validateStage1()) {
      setCurrentStage(2);
      setInputChecked(false);
    } else if (currentStage === 2 && validateStage2()) {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
      setInputChecked(false);
    }
  };

  const handleSubmit = async () => {
    if (!userData?.data?.id) {
      setSubmitError(t("failed_to_get_user_data"));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const allFormData = {
        personalInfo: {
          birthDate: birthDate ? format(birthDate, "yyyy-MM-dd") : null,
          birthPlace,
          registrationAddress,
          taxResidenceAddress,
          bankAccountNumber,
          taxOffice,
          nfzBranch,
        },
        declarations: {
          employment: {
            isEmployed,
            period: isEmployed
              ? {
                  from: employmentPeriodFrom
                    ? format(employmentPeriodFrom, "yyyy-MM-dd")
                    : null,
                  to: employmentPeriodTo
                    ? format(employmentPeriodTo, "yyyy-MM-dd")
                    : null,
                }
              : null,
            salaryLevel,
            onLeave,
            leavePeriod: onLeave
              ? {
                  from: leavePeriodFrom
                    ? format(leavePeriodFrom, "yyyy-MM-dd")
                    : null,
                  to: leavePeriodTo
                    ? format(leavePeriodTo, "yyyy-MM-dd")
                    : null,
                }
              : null,
          },
          otherContract: {
            hasOtherContract,
            period: hasOtherContract
              ? {
                  from: otherContractFrom
                    ? format(otherContractFrom, "yyyy-MM-dd")
                    : null,
                  to: otherContractTo
                    ? format(otherContractTo, "yyyy-MM-dd")
                    : null,
                }
              : null,
            exceedsMinimum: otherContractExceedsMinimum,
          },
          otherInsurance: {
            hasOtherInsurance,
            title: otherInsuranceTitle,
          },
          pension: {
            isPensioner,
            decisionNumber: pensionDecisionNumber,
          },
          disability: {
            hasDisability,
            level: disabilityLevel,
            period: hasDisability
              ? {
                  from: disabilityFrom
                    ? format(disabilityFrom, "yyyy-MM-dd")
                    : null,
                  to: disabilityTo ? format(disabilityTo, "yyyy-MM-dd") : null,
                }
              : null,
          },
          studentStatus: isStudent,
          unemploymentStatus: isUnemployed,
          socialInsuranceStatus: hasSocialInsurance,
          voluntaryInsurance: {
            health: wantsVoluntaryHealthInsurance,
            pension: wantsVoluntaryPensionInsurance,
          },
          taxExemption: wantsTaxExemption,
          taxResidencyCertificate: {
            hasCertificate: hasTaxResidencyCertificate,
            period: hasTaxResidencyCertificate
              ? {
                  from: taxResidencyCertificateFrom
                    ? format(taxResidencyCertificateFrom, "yyyy-MM-dd")
                    : null,
                  to: taxResidencyCertificateTo
                    ? format(taxResidencyCertificateTo, "yyyy-MM-dd")
                    : null,
                }
              : null,
          },
          authorCosts: {
            exceedsLimit: exceedsAuthorCostLimit,
            amount: authorCostAmount,
          },
          dataProcessingConsent,
        },
      };

      const payload = {
        clientId: userData.data.id,
        data: allFormData,
      };

      await createKwestionariush(payload);

      // Clear localStorage after successful submission
      localStorage.removeItem(FORM_STORAGE_KEY);

      // Invalidate user data to refresh the kwestionariush info
      queryClient.invalidateQueries(["userMe"]);

      // Show success message
      toast({
        title: t("success_title"),
        description: t("form_submitted_successfully"),
      });
      navigate("/account?tab=documents");
    } catch (error) {
      console.error("Error submitting kwestionariush:", error);
      const errorMessage =
        error.response?.data?.message || t("form_submission_error");
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegistrationBlur = () => {
  if (!taxResidenceAddress.trim() && registrationAddress.trim()) {
    setTaxResidenceAddress(registrationAddress);
  }
};

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="pb-4 md:pb-0 text-2xl font-bold text-center">
            {t("tax_questionnaire_title")}
          </CardTitle>
          <div className="mt-6">
            <KwestionariushProgressBar currentStage={currentStage} />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {currentStage === 1 && (
            <div className="space-y-0">
              <h3 className="text-lg font-semibold mb-4">
                {t("personal_data_stage_title")}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4">
                <div>
                  <DatePicker
                   placeholder={t("select_date_placeholder")}
                    label={t("birth_date_label")}
                    value={birthDate}
                    date={birthDate}
                    inputClassName={"h-12  bg-white"}
                    disabled={true}
                  />
                </div>

                <div className="pb-0">
                  <InputWithAlert
                    label={t("birth_place_label")}
                    value={birthPlace}
                    onChange={(e) => setBirthPlace(e.target.value)}
                    placeholder={t("birth_place_example")}
                     error={
                        inputChecked &&
                        (!birthPlace.trim() || !validateOnlyLatin(birthPlace))
                      }
                      errorMessage={
                        !birthPlace.trim()
                          ? t("birth_place_required")
                          : t("only_latin_invalid")
                      }
                    className="m-0 h-12"
                  />
                </div>
              </div>
              <div className="pb-4">
                <InputWithAlert
                  label={t("registration_address_label")}
                  value={registrationAddress}
                  onChange={(e) => setRegistrationAddress(e.target.value)}
                  placeholder={t("registration_address_example")}
                  error={
                      inputChecked &&
                      (!registrationAddress.trim() || !validateOnlyLatin(registrationAddress))
                    }
                    errorMessage={
                      !registrationAddress.trim()
                        ? t("registration_address_required")
                        : t("only_latin_invalid")
                    }
                  onBlur={handleRegistrationBlur}
                />
              </div>
              <div className="pb-4">
                <InputWithAlert
                  label={t("tax_residence_address_label")}
                  value={taxResidenceAddress}
                  onChange={(e) => setTaxResidenceAddress(e.target.value)}
                  placeholder={t("tax_residence_address_example")}
                   error={
                      inputChecked &&
                      (!taxResidenceAddress.trim() || !validateOnlyLatin(taxResidenceAddress))
                    }
                    errorMessage={
                      !taxResidenceAddress.trim()
                        ? t("tax_residence_address_required")
                        : t("signup:only_latin_invalid")
                    }
                />
              </div>
              <div className="pb-4">
                <InputWithAlert
                  label={t("bank_account_number_label")}
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  placeholder={t("bank_account_example")}
                  error={inputChecked && !bankAccountNumber.trim()}
                  errorMessage={t("bank_account_number_required")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <InputWithAlert
                    label={t("tax_office_label")}
                    value={taxOffice}
                    onChange={(e) => setTaxOffice(e.target.value)}
                    placeholder={t("tax_office_example")}
                    error={
                      inputChecked &&
                      (!taxOffice.trim() || !validateOnlyLatin(taxOffice))
                    }
                    errorMessage={
                      !taxOffice.trim()
                        ? t("tax_office_required")
                        : t("signup:only_latin_invalid")
                    }
                  />
                </div>

                <div>
                  <InputWithAlert
                    label={t("nfz_branch_label")}
                    value={nfzBranch}
                    onChange={(e) => setNfzBranch(e.target.value)}
                    placeholder={t("nfz_branch_example")}
                    error={
                      inputChecked &&
                      (!nfzBranch.trim() || !validateOnlyLatin(nfzBranch))
                    }
                    errorMessage={
                      !nfzBranch.trim()
                        ? t("nfz_branch_required")
                        : t("signup:only_latin_invalid")
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {currentStage === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold mb-4">
                {t("declarations_stage_title")}
              </h3>

              {/* Declaration 1 - Employment */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">1.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="employed"
                        checked={isEmployed}
                        onCheckedChange={setIsEmployed}
                      />
                      <Label htmlFor="employed" className="cursor-pointer">
                        {t("declaration_1_text")}
                      </Label>
                    </div>

                    {isEmployed && (
                      <div className="ml-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2">
                              {t("employment_period_from")}
                            </Label>
                            <DatePicker
                              date={employmentPeriodFrom}
                              setDate={setEmploymentPeriodFrom}
                              placeholder={t("employment_start_placeholder")}
                              error={
                                inputChecked &&
                                isEmployed &&
                                !employmentPeriodFrom
                              }
                              errorMessage={t("employment_start_required")}
                              disabled={{ after: new Date() }}
                            />
                          </div>
                          <div>
                            <Label className="mb-2">
                              {t("employment_period_to")}
                            </Label>
                            <DatePicker
                              date={employmentPeriodTo}
                              setDate={setEmploymentPeriodTo}
                              placeholder={t("employment_end_placeholder")}
                              error={
                                inputChecked &&
                                isEmployed &&
                                (!employmentPeriodTo ||
                                  (employmentPeriodFrom &&
                                    employmentPeriodTo &&
                                    isBefore(
                                      employmentPeriodTo,
                                      employmentPeriodFrom
                                    )))
                              }
                              errorMessage={
                                !employmentPeriodTo
                                  ? t("employment_end_required")
                                  : t("date_range_error")
                              }
                              disabled={{ before: employmentPeriodFrom }}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>{t("salary_level_text")}</Label>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="salary-minimum"
                                checked={salaryLevel === "minimum"}
                                onCheckedChange={(checked) =>
                                  setSalaryLevel(checked ? "minimum" : "")
                                }
                              />
                              <Label
                                htmlFor="salary-minimum"
                                className="cursor-pointer"
                              >
                                {t("salary_minimum")}
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="salary-below-minimum"
                                checked={salaryLevel === "below-minimum"}
                                onCheckedChange={(checked) =>
                                  setSalaryLevel(checked ? "below-minimum" : "")
                                }
                              />
                              <Label
                                htmlFor="salary-below-minimum"
                                className="cursor-pointer"
                              >
                                {t("salary_below_minimum")}
                              </Label>
                            </div>
                          </div>
                          {inputChecked && isEmployed && !salaryLevel && (
                            <p className="text-red-500 text-sm mt-2">
                              {t("salary_level_required")}
                            </p>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="on-leave"
                              checked={onLeave}
                              onCheckedChange={setOnLeave}
                            />
                            <Label
                              htmlFor="on-leave"
                              className="cursor-pointer"
                            >
                              {t("leave_text")}
                            </Label>
                          </div>

                          {onLeave && (
                            <div className="ml-6 grid grid-cols-2 gap-4">
                              <div>
                                <Label className="mb-2">
                                  {t("leave_from")}
                                </Label>
                                <DatePicker
                                  date={leavePeriodFrom}
                                  setDate={setLeavePeriodFrom}
                                  placeholder={t("leave_start_placeholder")}
                                  error={
                                    inputChecked && onLeave && !leavePeriodFrom
                                  }
                                  errorMessage={t("leave_start_required")}
                                  disabled={{ after: new Date() }}
                                />
                              </div>
                              <div>
                                <Label className="mb-2">{t("leave_to")}</Label>
                                <DatePicker
                                  date={leavePeriodTo}
                                  setDate={setLeavePeriodTo}
                                  placeholder={t("leave_end_placeholder")}
                                  error={
                                    inputChecked &&
                                    onLeave &&
                                    (!leavePeriodTo ||
                                      (leavePeriodFrom &&
                                        leavePeriodTo &&
                                        isBefore(
                                          leavePeriodTo,
                                          leavePeriodFrom
                                        )))
                                  }
                                  errorMessage={
                                    !leavePeriodTo
                                      ? t("leave_end_required")
                                      : t("date_range_error")
                                  }
                                  disabled={{ before: leavePeriodFrom }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration 2 - Other contracts */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">2.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="other-contract"
                        checked={hasOtherContract}
                        onCheckedChange={setHasOtherContract}
                      />
                      <Label
                        htmlFor="other-contract"
                        className="cursor-pointer"
                      >
                        {t("declaration_2_text")}
                      </Label>
                    </div>

                    {hasOtherContract && (
                      <div className="ml-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2">
                              {t("employment_period_from")}
                            </Label>
                            <DatePicker
                              date={otherContractFrom}
                              setDate={setOtherContractFrom}
                              placeholder={t("employment_start_placeholder")}
                              error={
                                inputChecked &&
                                hasOtherContract &&
                                !otherContractFrom
                              }
                              errorMessage={t("other_contract_start_required")}
                              disabled={{ after: new Date() }}
                            />
                          </div>
                          <div>
                            <Label className="mb-2">
                              {t("employment_period_to")}
                            </Label>
                            <DatePicker
                              date={otherContractTo}
                              setDate={setOtherContractTo}
                              placeholder={t("employment_end_placeholder")}
                              error={
                                inputChecked &&
                                hasOtherContract &&
                                (!otherContractTo ||
                                  (otherContractFrom &&
                                    otherContractTo &&
                                    isBefore(
                                      otherContractTo,
                                      otherContractFrom
                                    )))
                              }
                              errorMessage={
                                !otherContractTo
                                  ? t("other_contract_end_required")
                                  : t("date_range_error")
                              }
                              disabled={{ before: otherContractFrom }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="exceeds-minimum"
                            checked={otherContractExceedsMinimum}
                            onCheckedChange={setOtherContractExceedsMinimum}
                          />
                          <Label
                            htmlFor="exceeds-minimum"
                            className="cursor-pointer"
                          >
                            {t("contract_exceeds_minimum")}
                          </Label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration 3 - Other insurance */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">3.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="other-insurance"
                        checked={hasOtherInsurance}
                        onCheckedChange={setHasOtherInsurance}
                      />
                      <Label
                        htmlFor="other-insurance"
                        className="cursor-pointer"
                      >
                        {t("declaration_3_text")}
                      </Label>
                    </div>

                    {hasOtherInsurance && (
                      <div className="ml-6">
                        <InputWithAlert
                          label={t("insurance_title_required_text")}
                          value={otherInsuranceTitle}
                          onChange={(e) =>
                            setOtherInsuranceTitle(e.target.value)
                          }
                          placeholder={t("insurance_title_example")}
                          error={
                            inputChecked &&
                            hasOtherInsurance &&
                            !otherInsuranceTitle.trim()
                          }
                          errorMessage={t("insurance_title_required")}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration 4 - Pensioner */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">4.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pensioner"
                        checked={isPensioner}
                        onCheckedChange={setIsPensioner}
                      />
                      <Label htmlFor="pensioner" className="cursor-pointer">
                        {t("declaration_4_text")}
                      </Label>
                    </div>

                    {isPensioner && (
                      <div className="ml-6">
                        <InputWithAlert
                          label={t("pension_decision_text")}
                          value={pensionDecisionNumber}
                          onChange={(e) =>
                            setPensionDecisionNumber(e.target.value)
                          }
                          placeholder={t("pension_decision_example")}
                          error={
                            inputChecked &&
                            isPensioner &&
                            !pensionDecisionNumber.trim()
                          }
                          errorMessage={t("pension_decision_required_text")}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration 5 - Disability */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">5.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="disability"
                        checked={hasDisability}
                        onCheckedChange={setHasDisability}
                      />
                      <Label htmlFor="disability" className="cursor-pointer">
                        {t("declaration_5_text")}
                      </Label>
                    </div>

                    {hasDisability && (
                      <div className="ml-6 space-y-4">
                        <div className="space-y-2">
                          <Label>{t("disability_degree_text")}</Label>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="disability-light"
                                checked={disabilityLevel === "light"}
                                onCheckedChange={(checked) =>
                                  setDisabilityLevel(checked ? "light" : "")
                                }
                              />
                              <Label
                                htmlFor="disability-light"
                                className="cursor-pointer"
                              >
                                {t("disability_light")}
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="disability-moderate"
                                checked={disabilityLevel === "moderate"}
                                onCheckedChange={(checked) =>
                                  setDisabilityLevel(checked ? "moderate" : "")
                                }
                              />
                              <Label
                                htmlFor="disability-moderate"
                                className="cursor-pointer"
                              >
                                {t("disability_moderate")}
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="disability-severe"
                                checked={disabilityLevel === "severe"}
                                onCheckedChange={(checked) =>
                                  setDisabilityLevel(checked ? "severe" : "")
                                }
                              />
                              <Label
                                htmlFor="disability-severe"
                                className="cursor-pointer"
                              >
                                {t("disability_severe")}
                              </Label>
                            </div>
                          </div>
                          {inputChecked &&
                            hasDisability &&
                            !disabilityLevel && (
                              <p className="text-red-500 text-sm mt-2">
                                {t("disability_degree_required")}
                              </p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2">
                              {t("certificate_period_from")}
                            </Label>
                            <DatePicker
                              date={disabilityFrom}
                              setDate={setDisabilityFrom}
                              placeholder={t("certificate_from_placeholder")}
                              error={
                                inputChecked && hasDisability && !disabilityFrom
                              }
                              errorMessage={t(
                                "certificate_period_start_required"
                              )}
                              disabled={{ after: new Date() }}
                            />
                          </div>
                          <div>
                            <Label className="mb-2">
                              {t("certificate_period_to")}
                            </Label>
                            <DatePicker
                              date={disabilityTo}
                              setDate={setDisabilityTo}
                              placeholder={t("certificate_to_placeholder")}
                              error={
                                inputChecked &&
                                hasDisability &&
                                (!disabilityTo ||
                                  (disabilityFrom &&
                                    disabilityTo &&
                                    isBefore(disabilityTo, disabilityFrom)))
                              }
                              errorMessage={
                                !disabilityTo
                                  ? t("certificate_period_end_required")
                                  : t("date_range_error")
                              }
                              disabled={{ before: disabilityFrom }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declarations 6-8 */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold">6.</span>
                  <Checkbox
                    id="student"
                    checked={isStudent}
                    onCheckedChange={setIsStudent}
                  />
                  <Label htmlFor="student" className="cursor-pointer">
                    {t("declaration_6_text")}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="font-semibold">7.</span>
                  <Checkbox
                    id="unemployed"
                    checked={isUnemployed}
                    onCheckedChange={setIsUnemployed}
                    disabled={isEmployed}
                  />
                  <Label
                    htmlFor="unemployed"
                    className={
                      isEmployed
                        ? "cursor-not-allowed text-gray-400"
                        : "cursor-pointer"
                    }
                  >
                    {t("declaration_7_text")}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="font-semibold">8.</span>
                  <Checkbox
                    id="social-insurance"
                    checked={hasSocialInsurance}
                    onCheckedChange={setHasSocialInsurance}
                  />
                  <Label htmlFor="social-insurance" className="cursor-pointer">
                    {t("declaration_8_text")}
                  </Label>
                </div>

                {/* Voluntary insurance - nested under point 8 */}
                {hasSocialInsurance && (
                  <div className="ml-6 space-y-4">
                    <p className="font-semibold">
                      {t("voluntary_insurance_text")}
                    </p>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="voluntary-health"
                        checked={wantsVoluntaryHealthInsurance}
                        onCheckedChange={setWantsVoluntaryHealthInsurance}
                      />
                      <Label
                        htmlFor="voluntary-health"
                        className="cursor-pointer"
                      >
                        {t("voluntary_health_text")}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="voluntary-pension"
                        checked={wantsVoluntaryPensionInsurance}
                        onCheckedChange={setWantsVoluntaryPensionInsurance}
                      />
                      <Label
                        htmlFor="voluntary-pension"
                        className="cursor-pointer"
                      >
                        {t("voluntary_pension_text")}
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Declaration 9 - Tax exemption */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold">9.</span>
                  <Checkbox
                    id="tax-exemption"
                    checked={wantsTaxExemption}
                    onCheckedChange={setWantsTaxExemption}
                  />
                  <Label htmlFor="tax-exemption" className="cursor-pointer">
                    {t("declaration_9_text")}
                  </Label>
                </div>
              </div>

              {/* Declaration 10 - Tax residency certificate */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">10.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="tax-certificate"
                        checked={hasTaxResidencyCertificate}
                        onCheckedChange={setHasTaxResidencyCertificate}
                      />
                      <Label
                        htmlFor="tax-certificate"
                        className="cursor-pointer"
                      >
                        {t("declaration_10_text")}
                      </Label>
                    </div>

                    {hasTaxResidencyCertificate && (
                      <div className="ml-6 grid grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-2">
                            {t("certificate_period_from")}
                          </Label>
                          <DatePicker
                            date={taxResidencyCertificateFrom}
                            setDate={setTaxResidencyCertificateFrom}
                            placeholder={t("certificate_from_placeholder")}
                            error={
                              inputChecked &&
                              hasTaxResidencyCertificate &&
                              !taxResidencyCertificateFrom
                            }
                            errorMessage={t(
                              "certificate_period_start_required"
                            )}
                            disabled={{ after: new Date() }}
                          />
                        </div>
                        <div>
                          <Label className="mb-2">
                            {t("certificate_period_to")}
                          </Label>
                          <DatePicker
                            date={taxResidencyCertificateTo}
                            setDate={setTaxResidencyCertificateTo}
                            placeholder={t("certificate_to_placeholder")}
                            error={
                              inputChecked &&
                              hasTaxResidencyCertificate &&
                              (!taxResidencyCertificateTo ||
                                (taxResidencyCertificateFrom &&
                                  taxResidencyCertificateTo &&
                                  isBefore(
                                    taxResidencyCertificateTo,
                                    taxResidencyCertificateFrom
                                  )))
                            }
                            errorMessage={
                              !taxResidencyCertificateTo
                                ? t("certificate_period_end_required")
                                : t("date_range_error")
                            }
                            disabled={{ before: taxResidencyCertificateFrom }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration 11 - Author costs */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start space-x-2">
                  <span className="font-semibold">11.</span>
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="author-costs"
                        checked={exceedsAuthorCostLimit}
                        onCheckedChange={setExceedsAuthorCostLimit}
                      />
                      <Label htmlFor="author-costs" className="cursor-pointer">
                        {t("declaration_11_text")}
                      </Label>
                    </div>

                    {exceedsAuthorCostLimit && (
                      <div className="ml-6">
                        <InputWithAlert
                          label={t("author_costs_applied_text")}
                          value={authorCostAmount}
                          onChange={handleAuthorCostAmountChange}
                          placeholder={t("author_costs_example")}
                          error={
                            inputChecked &&
                            exceedsAuthorCostLimit &&
                            !authorCostAmount.trim()
                          }
                          errorMessage={t("author_cost_amount_required")}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Data processing consent */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="consent"
                    checked={dataProcessingConsent}
                    onCheckedChange={setDataProcessingConsent}
                  />
                  <Label htmlFor="consent" className="cursor-pointer text-sm">
                    {t("gdpr_consent_text")}
                  </Label>
                </div>
                {inputChecked && !dataProcessingConsent && (
                  <p className="text-red-500 text-sm mt-2">
                    {t("gdpr_consent_required")}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error display */}
          {submitError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{submitError}</p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStage === 1}
            >
              {t("back_button")}
            </Button>
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting
                ? t("submitting_button")
                : currentStage === 2
                ? t("submit_button")
                : t("next_button")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
